import React, { useState, useEffect, useCallback } from 'react';
import { registrationsApi, authApi, Event, RegistrationCreate, OrganizerProfile, emailTemplatesApi, EmailTemplateResponse } from '../api/client';
import { getErrorMessage } from '../utils/errorHandler';
import PastAttendeesSelector from './PastAttendeesSelector';
import EmailTemplateManager from './EmailTemplateManager';
import RichTextEditor from './email/RichTextEditor';
import { UserGroupIcon } from '@heroicons/react/24/outline';

interface InvitationManagerProps {
  event: Event;
  onClose?: () => void;
  onUpdate?: () => void;
  embedded?: boolean;
}

const InvitationManager: React.FC<InvitationManagerProps> = ({ event, onClose, onUpdate, embedded = false }) => {
  const [sending, setSending] = useState(false);
  const [organizerProfile, setOrganizerProfile] = useState<OrganizerProfile | null>(null);
  const [invitationTemplates, setInvitationTemplates] = useState<EmailTemplateResponse[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  
  // Form state for sending invitations
  const [inviteList, setInviteList] = useState<RegistrationCreate[]>([]);
  const [customMessage, setCustomMessage] = useState('');
  const [customSubject, setCustomSubject] = useState('');
  const [bulkInput, setBulkInput] = useState('');
  const [inputMode, setInputMode] = useState<'individual' | 'bulk'>('individual');
  const [messageTab, setMessageTab] = useState<'edit' | 'preview' | 'source'>('edit');
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showVariables, setShowVariables] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);  // New state for email toggle
  const [manualInvitationLinks, setManualInvitationLinks] = useState<Array<{name: string, email: string, link: string}>>([]);  // Store manual invitation links
  const [showPastAttendeesSelector, setShowPastAttendeesSelector] = useState(false);

  // Generate dynamic default template
  const getDefaultTemplate = useCallback(() => {
    return `Dear {{name}},

You're invited to {{event_name}}!

We would be delighted if you could join us at this exclusive event.

Event Details:
📅 Date & Time: {{event_date}}
📍 Venue: {{event_venue}}

This invitation is specifically for you as a representative of {{company}}.

We look forward to seeing you there!

Best regards,
{{organizer_name}}
{{organizer_company}}`;
  }, []);

  // Load organizer profile, relationship types, and templates
  const loadData = useCallback(async () => {
    try {
      const [profile, templates] = await Promise.all([
        authApi.getProfile(),
        emailTemplatesApi.listTemplates('invitation')
      ]);
      setOrganizerProfile(profile);
      setInvitationTemplates(templates);

      // Add initial empty invitation
      setInviteList([{ name: '', email: '', company: '' }]);

      // Set default template if available
      const defaultTemplate = templates.find(t => t.is_default);
      if (defaultTemplate) {
        setSelectedTemplateId(defaultTemplate.id);
        setCustomMessage(defaultTemplate.body);
        setCustomSubject(defaultTemplate.subject);
      } else if (templates.length > 0) {
        // Use first template if no default
        setSelectedTemplateId(templates[0].id);
        setCustomMessage(templates[0].body);
        setCustomSubject(templates[0].subject);
      } else {
        // Use plain text default if no templates exist
        const plainTextDefault = `Dear {{name}},

You're invited to {{event_name}}!

We would be delighted if you could join us at this exclusive event.

Event Details:
📅 Date & Time: {{event_date}}
📍 Venue: {{event_venue}}

This invitation is specifically for you as a representative of {{company}}.

We look forward to seeing you there!

Best regards,
{{organizer_name}}
{{organizer_company}}`;
        setCustomMessage(plainTextDefault);
        setCustomSubject(`You're invited to {{event_name}}!`);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  }, [getDefaultTemplate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle template selection change
  const handleTemplateChange = (templateId: number | null) => {
    setSelectedTemplateId(templateId);

    if (templateId === null) {
      // Custom message option selected
      setCustomMessage('');
      setCustomSubject('');
    } else if (templateId === -1) {
      // Use default template option
      setCustomMessage(getDefaultTemplate());
      setCustomSubject(`You're invited to {{event_name}}!`);
    } else {
      // Load selected template
      const template = invitationTemplates.find(t => t.id === templateId);
      if (template) {
        setCustomMessage(template.body);
        setCustomSubject(template.subject);
      }
    }
  };

  // Wrapper function to handle select change events
  const handleTemplateSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    const templateId = value === '' ? null : parseInt(value);
    handleTemplateChange(templateId);
  };

  const handleAddInvite = () => {
    setInviteList([...inviteList, { name: '', email: '', company: '' }]);
  };

  const handleImportPastAttendees = (importedAttendees: RegistrationCreate[]) => {
    // Merge with existing list, avoiding duplicates by email
    const existingEmails = new Set(inviteList.map(inv => inv.email?.toLowerCase()));
    const newAttendees = importedAttendees.filter(att => !existingEmails.has(att.email.toLowerCase()));

    if (newAttendees.length === 0) {
      alert('All selected attendees are already in the invitation list.');
      return;
    }

    // If the list only has one empty entry, replace it
    if (inviteList.length === 1 && !inviteList[0].email && !inviteList[0].name) {
      setInviteList(newAttendees);
    } else {
      setInviteList([...inviteList, ...newAttendees]);
    }
  };

  const handleRemoveInvite = (index: number) => {
    setInviteList(inviteList.filter((_, i) => i !== index));
  };

  const handleInviteChange = (index: number, field: keyof RegistrationCreate, value: string | number) => {
    const updated = [...inviteList];
    updated[index] = { ...updated[index], [field]: value };
    setInviteList(updated);
  };

  const parseBulkInput = (): RegistrationCreate[] => {
    const lines = bulkInput.trim().split('\n');
    const parsed: RegistrationCreate[] = [];

    for (const line of lines) {
      const parts = line.split(',').map(p => p.trim());
      if (parts.length >= 3) {
        parsed.push({
          name: parts[0],
          email: parts[1],
          company: parts[2]
        });
      }
    }

    return parsed;
  };

  const handleSendInvitations = async () => {
    setSending(true);
    try {
      const invitesToSend = inputMode === 'bulk' ? parseBulkInput() : inviteList.filter(inv => inv.email && inv.name && inv.company);
      
      if (invitesToSend.length === 0) {
        alert('Please add at least one valid invitation');
        return;
      }

      const result = await registrationsApi.sendInvitations(event.id, {
        registrations: invitesToSend,
        template_id: selectedTemplateId && selectedTemplateId > 0 ? selectedTemplateId : undefined,
        custom_message: customMessage || undefined,
        custom_subject: customSubject || undefined,
        send_email: sendEmail
      });

      // Handle manual invitations (no email sent)
      if (!sendEmail) {
        // Extract invitation links from the response
        const links = result.sent
          .filter(reg => reg.invitation_link)
          .map(reg => ({
            name: reg.name || '',
            email: reg.email,
            link: reg.invitation_link!
          }));
        
        setManualInvitationLinks(links);
        
        // Show success message
        alert(`Successfully created ${result.total_sent} invitation(s)! Copy the links below to share.`);
      } else {
        // Show appropriate message for email invitations
        if (result.total_skipped > 0) {
          const skippedEmails = result.skipped.map(s => `${s.email} (${s.reason})`).join('\n');
          alert(`Sent ${result.total_sent} invitation(s).\n\nSkipped ${result.total_skipped} duplicate(s):\n${skippedEmails}`);
        } else {
          alert(`Successfully sent ${result.total_sent} invitation(s)!`);
        }
      }
      
      // Reset form only if some invitations were sent/created
      if (result.total_sent > 0 && sendEmail) {
        setInviteList([{ name: '', email: '', company: '' }]);
        setBulkInput('');
        setCustomMessage(getDefaultTemplate());
        setSendEmail(true);  // Reset to default
        
        // Update parent component to refresh counts
        onUpdate?.();
      }
    } catch (error) {
      alert(getErrorMessage(error));
    } finally {
      setSending(false);
    }
  };



  const content = (
    <>
      {!embedded && (
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Manage Invitations - {event.title}</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>

          <p className="text-gray-600 mt-2">Send invitations to potential attendees. All sent invitations will appear in the main Requests tab.</p>
        </div>
      )}

      {embedded && (
        <div className="mb-6">
          <p className="text-sm text-gray-600">Send invitations to potential attendees. All sent invitations will appear in the main Requests tab.</p>
        </div>
      )}

      <div className={embedded ? "space-y-6" : "p-6 overflow-y-auto max-h-[calc(90vh-200px)]"}>
        {/* Send invitation form */}
        <div className="space-y-6">
          {/* Input mode selector */}
          <div className="flex justify-between items-center">
            <div className="flex gap-4">
              <button
                onClick={() => setInputMode('individual')}
                className={`px-4 py-2 rounded-lg font-medium ${
                  inputMode === 'individual'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Individual Entry
              </button>
              <button
                onClick={() => setInputMode('bulk')}
                className={`px-4 py-2 rounded-lg font-medium ${
                  inputMode === 'bulk'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Bulk Import (CSV)
              </button>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowMessageModal(true)}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
              >
                📝 Customise Message
              </button>
              <button
                onClick={() => setShowTemplateManager(true)}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
              >
                📑 Templates
              </button>
            </div>
          </div>

          {/* Template selector */}
          <div className="mt-4 flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700">
              Email Template:
            </label>
            <select
              value={selectedTemplateId || ''}
              onChange={handleTemplateSelectChange}
              className="flex-1 max-w-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">-- Select Template --</option>
              {invitationTemplates.map(template => (
                <option key={template.id} value={template.id}>{template.name}</option>
              ))}
            </select>
          </div>

          {/* Individual entry form */}
          {inputMode === 'individual' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium">Invitation List</h3>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setShowPastAttendeesSelector(true)}
                    className="inline-flex items-center px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium"
                  >
                    <UserGroupIcon className="h-4 w-4 mr-1.5" />
                    Import from Past Events
                  </button>
                  <button
                    onClick={handleAddInvite}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    + Add Another
                  </button>
                </div>
              </div>
              
              {inviteList.map((invite, index) => (
                <div key={index} className="grid grid-cols-11 gap-3 items-center">
                  <div className="col-span-3">
                    <input
                      type="text"
                      placeholder="Full Name"
                      value={invite.name || ''}
                      onChange={(e) => handleInviteChange(index, 'name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div className="col-span-4">
                    <input
                      type="email"
                      placeholder="Email Address"
                      value={invite.email || ''}
                      onChange={(e) => handleInviteChange(index, 'email', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div className="col-span-3">
                    <input
                      type="text"
                      placeholder="Company"
                      value={invite.company || ''}
                      onChange={(e) => handleInviteChange(index, 'company', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div className="col-span-1 text-center">
                    {inviteList.length > 1 && (
                      <button
                        onClick={() => handleRemoveInvite(index)}
                        className="text-red-600 hover:text-red-800"
                        title="Remove"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Bulk import form */}
          {inputMode === 'bulk' && (
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-900 font-medium mb-2">CSV Format:</p>
                <p className="text-sm text-blue-700">One invitation per line: Name, Email, Company</p>
                <p className="text-xs text-blue-600 mt-1">Example: John Doe, john@example.com, Acme Corp</p>
              </div>
              
              <textarea
                value={bulkInput}
                onChange={(e) => setBulkInput(e.target.value)}
                placeholder="Paste your CSV data here..."
                className="w-full h-48 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono text-sm"
              />
              
              {bulkInput && (
                <div className="text-sm text-gray-600">
                  {parseBulkInput().length} valid invitation(s) detected
                </div>
              )}
            </div>
          )}


          {/* Email toggle and Send button */}
          <div className="flex justify-between items-center pt-4 border-t">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm font-medium text-gray-700">Send email automatically</span>
            </label>
            
            <button
              onClick={() => {
                const count = inputMode === 'bulk' ? parseBulkInput().length : inviteList.filter(inv => inv.email && inv.name && inv.company).length;
                if (count === 0) {
                  alert('Please add at least one valid invitation');
                  return;
                }
                const action = sendEmail ? 'send' : 'create';
                if (window.confirm(`${action === 'send' ? 'Send' : 'Create'} ${count} invitation(s)?`)) {
                  handleSendInvitations();
                }
              }}
              disabled={sending}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {sending ? (sendEmail ? 'Sending...' : 'Creating...') : (sendEmail ? 'Send' : 'Create')}
            </button>
          </div>
          
          {/* Display manual invitation links */}
          {manualInvitationLinks.length > 0 && (
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <h3 className="font-semibold text-blue-900 mb-3">Invitation Links Created</h3>
              <p className="text-sm text-blue-700 mb-3">Copy these links to share with invitees:</p>
              <div className="space-y-2">
                {manualInvitationLinks.map((link, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-white rounded border border-blue-200">
                    <div className="flex-1">
                      <span className="font-medium text-gray-900">{link.name}</span>
                      <span className="text-gray-600 ml-2">({link.email})</span>
                      <div className="text-xs text-gray-500 mt-1 break-all">{link.link}</div>
                    </div>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(link.link);
                        alert('Link copied to clipboard!');
                      }}
                      className="ml-2 px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Copy Link
                    </button>
                  </div>
                ))}
              </div>
              <button
                onClick={() => {
                  // Reset form and clear links
                  setInviteList([{ name: '', email: '', company: '' }]);
                  setBulkInput('');
                  setManualInvitationLinks([]);
                  setSendEmail(true);
                  onUpdate?.();
                }}
                className="mt-3 text-sm text-blue-600 hover:text-blue-800 underline"
              >
                Create More Invitations
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Message Template Modal */}
      {showMessageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">Customise Invitation Message</h2>
                <button
                  onClick={() => setShowMessageModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              {/* Variable reference - collapsible */}
              <div className="mb-6">
                <button
                  onClick={() => setShowVariables(!showVariables)}
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  <span>{showVariables ? '▼' : '▶'}</span>
                  {showVariables ? 'Hide' : 'Show'} available variables
                </button>
                {showVariables && (
                  <div className="mt-3 bg-gray-50 p-4 rounded-lg text-sm">
                    <div className="grid grid-cols-2 gap-2 text-gray-600">
                      <div><code className="bg-gray-200 px-1 rounded">{'{{name}}'}</code> - Recipient's name</div>
                      <div><code className="bg-gray-200 px-1 rounded">{'{{company}}'}</code> - Recipient's company</div>
                      <div><code className="bg-gray-200 px-1 rounded">{'{{event_name}}'}</code> - Event title</div>
                      <div><code className="bg-gray-200 px-1 rounded">{'{{event_date}}'}</code> - Event date & time</div>
                      <div><code className="bg-gray-200 px-1 rounded">{'{{event_venue}}'}</code> - Event venue</div>
                      <div><code className="bg-gray-200 px-1 rounded">{'{{google_maps_link}}'}</code> - Google Maps link</div>
                      <div><code className="bg-gray-200 px-1 rounded">{'{{organizer_name}}'}</code> - Your name</div>
                      <div><code className="bg-gray-200 px-1 rounded">{'{{organizer_company}}'}</code> - Your company</div>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject Line
                  </label>
                  <input
                    type="text"
                    value={customSubject}
                    onChange={(e) => setCustomSubject(e.target.value)}
                    placeholder="You're invited to {{event_name}}!"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message
                  </label>
                  <div className="flex gap-2 mb-2">
                    <button
                      onClick={() => setMessageTab('edit')}
                      className={`px-3 py-1 text-sm font-medium rounded ${
                        messageTab === 'edit'
                          ? 'bg-gray-200 text-gray-900'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Rich Text
                    </button>
                    <button
                      onClick={() => setMessageTab('source')}
                      className={`px-3 py-1 text-sm font-medium rounded ${
                        messageTab === 'source'
                          ? 'bg-gray-200 text-gray-900'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Source
                    </button>
                    <button
                      onClick={() => setMessageTab('preview')}
                      className={`px-3 py-1 text-sm font-medium rounded ${
                        messageTab === 'preview'
                          ? 'bg-gray-200 text-gray-900'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Preview
                    </button>
                  </div>

                  {messageTab === 'edit' ? (
                    <RichTextEditor
                      content={customMessage}
                      onChange={(html) => setCustomMessage(html)}
                      placeholder="Compose your invitation message..."
                    />
                  ) : messageTab === 'source' ? (
                    <textarea
                      value={customMessage}
                      onChange={(e) => setCustomMessage(e.target.value)}
                      rows={12}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono text-sm"
                      placeholder="<p>Dear {{name}},</p>..."
                    />
                  ) : (
                    <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                      <div
                        className="prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{
                          __html: customMessage.replace(/\{\{(\w+)\}\}/g, (match, variable) => {
                            const previewValues: { [key: string]: string } = {
                              name: 'John Doe',
                              company: 'Example Corp',
                              event_name: event.title,
                              event_date: new Date(event.date_time).toLocaleString(),
                              event_venue: event.venue,
                              google_maps_link: event.google_maps_link || '',
                              organizer_name: organizerProfile?.name || 'Your Name',
                              organizer_company: organizerProfile?.company || 'Your Company'
                            };
                            return previewValues[variable] || match;
                          })
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => setShowMessageModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {content}
        </div>
      </div>

      {/* Past Attendees Selector Modal */}
      {showPastAttendeesSelector && (
        <PastAttendeesSelector
          event={event}
          onSelect={handleImportPastAttendees}
          onClose={() => setShowPastAttendeesSelector(false)}
        />
      )}

      {/* Template Manager Modal */}
      {showTemplateManager && (
        <EmailTemplateManager
          category="invitation"
          onClose={() => {
            setShowTemplateManager(false);
            // Reload templates after closing
            loadData();
          }}
        />
      )}

    </>
  );
};

export default InvitationManager;